export interface ChromeExtensionUpdateFeatureFlagPayload {
  helpLink: string | null
  minVersion: string
}

export interface ChromeExtensionUpdateRequirement {
  helpLink: string | null
  minVersion: string | null
  requiresUpdate: boolean
}

interface ChromeExtensionUpdateRequirementInput {
  featureFlagEnabled: boolean | undefined
  featureFlagPayload: unknown
  installedVersion: string | null
}

function parseVersion(version: string): number[] | null {
  const normalizedVersion = version.trim()
  if (!normalizedVersion) return null

  const parts = normalizedVersion.split(".")
  if (parts.length === 0) return null

  const parsedParts = parts.map((part) => {
    if (!/^\d+$/.test(part)) return null
    return Number.parseInt(part, 10)
  })

  return parsedParts.some((part) => part === null) ? null : (parsedParts as number[])
}

export function compareChromeExtensionVersions(left: string, right: string): number | null {
  const leftParts = parseVersion(left)
  const rightParts = parseVersion(right)

  if (!leftParts || !rightParts) return null

  const length = Math.max(leftParts.length, rightParts.length)

  for (let index = 0; index < length; index += 1) {
    const leftPart = leftParts[index] ?? 0
    const rightPart = rightParts[index] ?? 0

    if (leftPart > rightPart) return 1
    if (leftPart < rightPart) return -1
  }

  return 0
}

export function parseChromeExtensionUpdatePayload(payload: unknown): ChromeExtensionUpdateFeatureFlagPayload | null {
  let normalizedPayload = payload

  if (typeof normalizedPayload === "string") {
    try {
      normalizedPayload = JSON.parse(normalizedPayload)
    } catch {
      return null
    }
  }

  if (!normalizedPayload || typeof normalizedPayload !== "object") return null

  const minVersion = "minVersion" in normalizedPayload && typeof normalizedPayload.minVersion === "string" ? normalizedPayload.minVersion.trim() : ""
  if (!minVersion) return null

  const helpLink =
    "helpLink" in normalizedPayload && typeof normalizedPayload.helpLink === "string" && normalizedPayload.helpLink.trim() ? normalizedPayload.helpLink.trim() : null

  return {
    minVersion,
    helpLink,
  }
}

export function getChromeExtensionUpdateRequirement({
  featureFlagEnabled,
  featureFlagPayload,
  installedVersion,
}: ChromeExtensionUpdateRequirementInput): ChromeExtensionUpdateRequirement {
  if (featureFlagEnabled !== true) {
    return {
      minVersion: null,
      helpLink: null,
      requiresUpdate: false,
    }
  }

  const parsedPayload = parseChromeExtensionUpdatePayload(featureFlagPayload)
  if (!parsedPayload) {
    return {
      minVersion: null,
      helpLink: null,
      requiresUpdate: false,
    }
  }

  if (!parseVersion(parsedPayload.minVersion)) {
    return {
      minVersion: parsedPayload.minVersion,
      helpLink: parsedPayload.helpLink,
      requiresUpdate: false,
    }
  }

  if (!installedVersion) {
    return {
      minVersion: parsedPayload.minVersion,
      helpLink: parsedPayload.helpLink,
      requiresUpdate: true,
    }
  }

  const comparison = compareChromeExtensionVersions(installedVersion, parsedPayload.minVersion)

  return {
    minVersion: parsedPayload.minVersion,
    helpLink: parsedPayload.helpLink,
    requiresUpdate: comparison === null ? true : comparison < 0,
  }
}
